import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/config';
import { login as loginApi, register as registerApi, getCurrentUser } from '../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string; 
  isAdmin?: boolean;
  role?: string;
  firstName?: string;
  notificationPreferences?: {
    email: boolean;
    sms: boolean;
    push: boolean;
    orderUpdates: boolean;
    promotions: boolean;
  };
  lastLogin?: string;
}

interface LoginCredentials {
  email?: string;
  phone?: string;
  password: string;
}

interface RegisterData {
  name: string;
  email?: string; // Made optional for phone-only registration
  phone?: string;
  password: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  push: boolean;
  orderUpdates: boolean;
  promotions: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string; user?: User }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string; user?: User }>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  getUserFirstName: () => string;
  updateProfile: (data: { name?: string; phone?: string }) => Promise<{ success: boolean; error?: string }>;
  changePassword: (data: ChangePasswordData) => Promise<{ success: boolean; error?: string }>;
  updateNotificationPreferences: (preferences: Partial<NotificationPreferences>) => Promise<{ success: boolean; error?: string }>;
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;
  forgotPassword: (
    identifier: string
  ) => Promise<{
    success: boolean;
    error?: string;
  }>;

  resetPassword: (
    identifier: string,
    verificationCode: string,
    newPassword: string
  ) => Promise<{
    success: boolean;
    error?: string;
  }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to determine if user is admin
  const checkIsAdmin = (user: User | null): boolean => {
    if (!user) return false;

    return (
      user.isAdmin === true ||
      user.role?.toLowerCase() === 'admin'
    );
  };

  // Helper to get user's first name
  const getUserFirstName = (): string => {
    if (!user) return 'User';
    return user.firstName || user.name?.split(' ')[0] || 'User';
  };

  // Helper to extract first name from full name
  const extractFirstName = (fullName: string): string => {
    return fullName?.split(' ')[0] || 'User';
  };

  // Configure axios defaults for persistent authentication
  useEffect(() => {
    // Set default headers for all axios requests
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    // Add response interceptor to handle token expiration
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await getCurrentUser();
          const userData = response.data.user;
          
          // Ensure isAdmin is properly set from API response
          const enrichedUser = {
            ...userData,
            firstName: extractFirstName(userData.name),
            isAdmin:
              userData.isAdmin === true ||
              userData.role?.toLowerCase() === 'admin'
          };
          
          setUser(enrichedUser);
          console.log('User loaded:', enrichedUser.email || enrichedUser.phone, 'IsAdmin:', enrichedUser.isAdmin);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    console.log('=== LOGIN ATTEMPT ===');
    console.log('Credentials:', credentials.email || credentials.phone);
    console.log('API_URL:', API_URL);
    
    try {
      const response = await loginApi(credentials);
      console.log('Login response:', response.data);
      const { token, user: userData } = response.data;
      
      // Enrich user data with admin status and first name
      const enrichedUser = {
        ...userData,
        firstName: extractFirstName(userData.name),
        isAdmin:
          userData.isAdmin === true ||
          userData.role?.toLowerCase() === 'admin'
      };
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(enrichedUser);
      console.log('Login successful! IsAdmin:', enrichedUser.isAdmin);
      return { success: true, user: enrichedUser };
    } catch (error: any) {
      console.error('Login error details:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed. Please check your credentials.',
        user: undefined
      };
    }
  };

  const register = async (data: RegisterData) => {
    console.log('=== REGISTRATION ATTEMPT ===');
    console.log('Email:', data.email);
    console.log('Phone:', data.phone);
    
    // Validate that at least email or phone is provided
    if (!data.email && !data.phone) {
      return { 
        success: false, 
        error: 'Either email or phone number is required',
        user: undefined
      };
    }
    
    try {
      const response = await registerApi(data);
      const { token, user: userData } = response.data;
      
      // Regular users are never admin on registration
      const enrichedUser = {
        ...userData,
        firstName: extractFirstName(userData.name),
        isAdmin: false
      };
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(enrichedUser);
      console.log('Registration successful!');
      return { success: true, user: enrichedUser };
    } catch (error: any) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed. Email or phone may already be in use.',
        user: undefined
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    console.log('User logged out');
  };

  const updateProfile = async (data: { name?: string; phone?: string }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/auth/profile`,
        data,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const updatedUser = {
          ...user!,
          ...response.data.user,
          firstName: extractFirstName(response.data.user.name || user!.name)
        };
        setUser(updatedUser);
        return { success: true };
      }
      return { success: false, error: response.data.error };
    } catch (error: any) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update profile'
      };
    }
  };

  const changePassword = async (data: { currentPassword: string; newPassword: string }) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Changing password for user...');
      
      const response = await axios.put(
        `${API_URL}/auth/change-password`,
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword
        },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log('Password change response:', response.data);
      
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, error: response.data.error };
    } catch (error: any) {
      console.error('Password change error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to change password';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const updateNotificationPreferences = async (preferences: Partial<NotificationPreferences>) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/auth/notifications`,
        preferences,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setUser(prev => prev ? {
          ...prev,
          notificationPreferences: response.data.notificationPreferences
        } : null);
        return { success: true };
      }
      return { success: false, error: response.data.error };
    } catch (error: any) {
      console.error('Notification update error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update notification preferences'
      };
    }
  };

  const deleteAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/auth/account`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        return { success: true };
      }
      return { success: false, error: response.data.error };
    } catch (error: any) {
      console.error('Account deletion error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to delete account'
      };
    }
  };

  const forgotPassword = async (identifier: string) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/forgot-password`,
        {
          identifier
        }
      );

      if (response.data.success) {
        return {
          success: true
        };
      }

      return {
        success: false,
        error: response.data.error
      };
    } catch (error: any) {
      console.error('Forgot password error:', error);

      return {
        success: false,
        error:
          error.response?.data?.error ||
          'Unable to send verification code'
      };
    }
  };

  const resetPassword = async (
    identifier: string,
    verificationCode: string,
    newPassword: string
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/reset-password`,
        {
          identifier,
          verificationCode,
          newPassword
        }
      );

      if (response.data.success) {
        return {
          success: true
        };
      }

      return {
        success: false,
        error: response.data.error
      };
    } catch (error: any) {
      console.error('Reset password error:', error);

      return {
        success: false,
        error:
          error.response?.data?.error ||
          'Unable to reset password'
      };
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user,  
        loading, 
        login, 
        register, 
        logout, 
        isAuthenticated: !!user,
        isAdmin: checkIsAdmin(user),
        getUserFirstName,
        updateProfile,
        changePassword,
        updateNotificationPreferences,
        deleteAccount,
        forgotPassword,
        resetPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};